// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

import { useState, useEffect, useCallback } from 'react';
import api from '../api';

/**
 * Tree node component for rendering hierarchical sheets
 * Supports nested hierarchies with the same file used in multiple instances
 */
function SheetTreeNode({ node, level = 0, selectedPath, onSelect, commentCounts, instancePath = '' }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedPath === node.path;
  const commentCount = commentCounts[node.path] || 0;

  // Create unique instance path for this node (handles same file used multiple times)
  const currentInstancePath = instancePath ? `${instancePath}/${node.name}` : node.name;

  return (
    <div>
      <div
        className={`
          nav-item group
          ${isSelected ? 'active' : ''}
        `}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelect(node.path, currentInstancePath)}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="w-5 h-5 flex items-center justify-center text-st-muted hover:text-st-accent transition-colors"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            <svg
              className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        ) : (
          <span className="w-5 h-5"></span>
        )}

        {/* Sheet name */}
        <span className="flex-1 truncate text-sm font-body" title={`${node.name}\n${node.path}`}>
          {node.name}
        </span>

        {/* Comment count badge */}
        {commentCount > 0 && (
          <span className="px-1.5 py-0.5 text-xs bg-st-accent/30 text-st-accent min-w-[20px] text-center font-mono font-semibold">
            {commentCount}
          </span>
        )}

        {/* Circular reference warning */}
        {node.circular && (
          <span className="text-xs text-st-warning" title="Circular reference - already shown above">
            <i className="bx bx-refresh"></i>
          </span>
        )}

        {/* Error indicator */}
        {node.error && (
          <span className="text-xs text-st-error" title={node.error}>
            <i className="bx bx-error"></i>
          </span>
        )}
      </div>

      {/* Children - shown when expanded */}
      {hasChildren && expanded && (
        <div className="border-l border-st-border-subtle ml-4">
          {node.children.map((child, index) => (
            <SheetTreeNode
              key={`${child.path}-${index}-${child.name}`}
              node={child}
              level={level + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
              commentCounts={commentCounts}
              instancePath={currentInstancePath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Flat sheet list for non-hierarchical view
 */
function FlatSheetList({ sheets, selectedPath, onSelect, commentCounts }) {
  return (
    <div className="space-y-1">
      {sheets.map((sheet) => {
        const isSelected = selectedPath === sheet.path;
        const commentCount = commentCounts[sheet.path] || 0;

        return (
          <div
            key={sheet.path}
            className={`nav-item ${isSelected ? 'active' : ''}`}
            onClick={() => onSelect(sheet.path)}
          >
            <span className="flex-1 truncate text-sm font-body" title={sheet.path}>
              {sheet.name}
            </span>
            {sheet.changed_in_mr && (
              <span className="px-1.5 py-0.5 text-xs bg-st-warning/20 text-st-warning font-mono font-semibold uppercase">
                Modified
              </span>
            )}
            {commentCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-st-accent/30 text-st-accent font-mono font-semibold">
                {commentCount}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * SheetSelector component for navigating hierarchical schematics
 */
export default function SheetSelector({
  files,
  selectedFile,
  onSelectFile,
  comments = [],
  projectId,
  mrIid
}) {
  const [hierarchy, setHierarchy] = useState(null);
  const [viewMode, setViewMode] = useState('hierarchy'); // 'hierarchy' or 'flat'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Find root schematic
  const rootSchematic = files.find(f => f.is_root && f.file_type === 'schematic');
  const schematicFiles = files.filter(f => f.file_type === 'schematic');
  const pcbFiles = files.filter(f => f.file_type === 'pcb');

  // Build comment counts by file path
  const commentCounts = {};
  comments.forEach(c => {
    const path = c.file_path;
    commentCounts[path] = (commentCounts[path] || 0) + 1;
  });

  // Load hierarchy when root schematic is available
  useEffect(() => {
    async function loadHierarchy() {
      if (!rootSchematic || !projectId || !mrIid) {
        setHierarchy(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('Loading hierarchy for:', rootSchematic.path);
        const data = await api.getSheetHierarchy(rootSchematic.path, mrIid, projectId);
        console.log('Hierarchy data received:', JSON.stringify(data, null, 2));
        setHierarchy(data.root);
      } catch (err) {
        console.error('Failed to load sheet hierarchy:', err);
        setError(err.message);
        // Fall back to flat view on error
        setViewMode('flat');
      } finally {
        setLoading(false);
      }
    }

    loadHierarchy();
  }, [rootSchematic?.path, projectId, mrIid]);

  const handleSelectSheet = useCallback((path, instancePath = null) => {
    const file = files.find(f => f.path === path);
    if (file) {
      onSelectFile(file);
    } else {
      // File might not be in the changed files list, create a minimal file object
      onSelectFile({
        path: path,
        new_path: path,
        name: path.split('/').pop(),
        file_type: 'schematic',
        instance_path: instancePath // For tracking which instance was selected
      });
    }
  }, [files, onSelectFile]);

  const selectedPath = selectedFile?.path || selectedFile?.new_path;

  // Don't show if no schematic files
  if (schematicFiles.length === 0) {
    return null;
  }

  return (
    <div className="bg-st-primary border-b-2 border-st-border">
      {/* Header with view toggle */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-st-border-subtle">
        <span className="text-xs font-display font-semibold text-st-muted uppercase tracking-wider">
          Schematic Sheets
        </span>

        {hierarchy && schematicFiles.length > 1 && (
          <button
            onClick={() => setViewMode(viewMode === 'hierarchy' ? 'flat' : 'hierarchy')}
            className="btn btn-sm text-xs"
            style={{ padding: '4px 8px', border: '2px solid rgb(var(--st-border))', background: 'rgb(var(--st-bg-tertiary))', color: 'rgb(var(--st-text-primary))' }}
          >
            {viewMode === 'hierarchy' ? 'Tree' : 'Flat'}
          </button>
        )}
      </div>

      {/* Sheet list */}
      <div className="max-h-[400px] overflow-y-auto py-1">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="spinner w-4 h-4" />
            <span className="ml-2 text-sm text-st-muted font-body">Loading hierarchy...</span>
          </div>
        ) : error ? (
          <div className="px-3 py-2 text-xs text-st-warning font-body">
            <i className="bx bx-error"></i> Could not load hierarchy: {error}
          </div>
        ) : viewMode === 'hierarchy' && hierarchy ? (
          <SheetTreeNode
            node={hierarchy}
            selectedPath={selectedPath}
            onSelect={handleSelectSheet}
            commentCounts={commentCounts}
          />
        ) : (
          <FlatSheetList
            sheets={schematicFiles}
            selectedPath={selectedPath}
            onSelect={handleSelectSheet}
            commentCounts={commentCounts}
          />
        )}
      </div>

      {/* PCB files section */}
      {pcbFiles.length > 0 && (
        <>
          <div className="px-3 py-2 border-t-2 border-st-border">
            <span className="text-xs font-display font-semibold text-st-muted uppercase tracking-wider">
              PCB Layout
            </span>
          </div>
          <div className="pb-1">
            <FlatSheetList
              sheets={pcbFiles}
              selectedPath={selectedPath}
              onSelect={handleSelectSheet}
              commentCounts={commentCounts}
            />
          </div>
        </>
      )}
    </div>
  );
}
